import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';

// Hook général pour les permissions
export const usePermissions = () => {
  const { user, isAuthenticated } = useAuth();
  const { 
    currentSubscription, 
    canManageFAQs, 
    canConfigureWhatsApp,
    hasPlan,
    hasFeature,
    subscriptionTier 
  } = useSubscriptionContext();

  return {
    // Permissions générales
    isAuthenticated,
    isAdmin: user?.is_staff || false,
    isSuperuser: user?.is_superuser || false,
    
    // Permissions par subscription
    canManageFAQs,
    canConfigureWhatsApp,
    hasPlan,
    hasFeature,
    
    // Informations utiles
    subscriptionTier,
    currentSubscription,
    userId: user?.id,
    userEmail: user?.email,
    
    // Helpers
    requiresUpgrade: (requiredPlan: string) => {
      if (!currentSubscription) return true;
      return !hasPlan(requiredPlan);
    },
    
    hasActiveSubscription: () => {
      return currentSubscription?.is_active ?? false;
    }
  };
};

// Hook spécialisé pour FAQ
export const useFAQPermissions = () => {
  const { canManageFAQs, currentSubscription } = useSubscriptionContext();
  const { isAuthenticated } = useAuth();

  return {
    canCreateFAQ: canManageFAQs,
    canEditFAQ: canManageFAQs,
    canDeleteFAQ: canManageFAQs,
    canTestAgent: canManageFAQs,
    canBulkImport: canManageFAQs,
    canExportFAQs: canManageFAQs,
    canViewStats: canManageFAQs,
    
    // Métadonnées utiles
    maxFAQsAllowed: getFAQLimit(currentSubscription?.plan.name),
    subscriptionId: currentSubscription?.id,
    
    // Validation
    validateFAQOperation: (operation: string) => {
      if (!isAuthenticated) return { allowed: false, reason: 'Non authentifié' };
      if (!canManageFAQs) return { allowed: false, reason: 'Abonnement FAQ WhatsApp requis' };
      return { allowed: true, reason: null };
    }
  };
};

// Hook spécialisé pour WhatsApp
export const useWhatsAppPermissions = () => {
  const { canConfigureWhatsApp, currentSubscription } = useSubscriptionContext();
  const { isAuthenticated } = useAuth();

  return {
    canConfigureNumber: canConfigureWhatsApp,
    canSetupTokens: canConfigureWhatsApp,
    canTestWebhook: canConfigureWhatsApp,
    canManageAgent: canConfigureWhatsApp,
    canViewMetrics: canConfigureWhatsApp,
    
    // Validation
    validateWhatsAppOperation: (operation: string) => {
      if (!isAuthenticated) return { allowed: false, reason: 'Non authentifié' };
      if (!canConfigureWhatsApp) return { allowed: false, reason: 'Abonnement FAQ WhatsApp requis' };
      return { allowed: true, reason: null };
    }
  };
};

// Utilitaire pour limites par plan
const getFAQLimit = (planName?: string): number => {
  if (!planName) return 0;
  
  switch (planName.toLowerCase()) {
    case 'faq whatsapp':
    case 'basic':
      return 100;
    case 'premium':
      return 500;
    case 'enterprise':
      return 1000;
    default:
      return 0;
  }
};

